import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Refine } from "@refinedev/core";
import routerBindings from "@refinedev/react-router-v6";
import { dataProvider } from "@refinedev/supabase";
import { SupabaseProvider, useSupabase } from "./hooks/useSupabase";
import { LanguageProvider } from "./providers/languageProvider";
import authProvider from "./providers/authProvider";
import App from "./pages/App";
import { validateEnvironment } from "./config/env";
import { ProtectedRoute, AuthErrorBoundary } from "./components";

import "./styles.css";
// Validate environment variables early
validateEnvironment();

const AppWithRefine: React.FC = () => {
  const supabase = useSupabase();

  return (
    <Refine
      dataProvider={dataProvider(supabase)}
      authProvider={authProvider(supabase)}
      routerProvider={routerBindings}
      resources={[
        {
          name: "post",
          list: "/post",
          create: "/post/new",
          edit: "/post/edit/:id",
          show: "/post/show/:id",
          meta: {
            icon: "📝",
            label: "Posts",
          },
        },
        {
          name: "comments",
          list: "/comments",
          create: "/comments/create",
          edit: "/comments/edit/:id",
          show: "/comments/show/:id",
          meta: {
            icon: "💬",
            label: "Comments",
          },
        },
      ]}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
        useNewQueryKeys: true,
      }}
    >
      <ProtectedRoute>
        <App />
      </ProtectedRoute>
    </Refine>
  );
};

const Main: React.FC = () => (
  <AuthErrorBoundary>
    <SupabaseProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AppWithRefine />
        </BrowserRouter>
      </LanguageProvider>
    </SupabaseProvider>
  </AuthErrorBoundary>
);

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(<Main />);
}
